import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import {
  getAssessmentsByStudentId,
  getStudentDuplicateResults,
  searchTeacherStudents,
  unassignAssessmentBatch
} from "../services/TeacherAPIClient";
import { formatClassDisplayName } from "../utils/classDisplay";
import "./TeacherFixDuplicateResultsModal.css";

const STUDENT_SEARCH_MIN_LENGTH = 2;
const STUDENT_SEARCH_LIMIT = 25;
const STUDENT_SEARCH_DEBOUNCE_MS = 300;
const STILL_DUPLICATED_ERROR =
  "Duplicate results were not changed. The same duplicate results are still being returned for this child.";
const MISSING_ASSESSMENT_INSTANCE_ERROR =
  "This duplicate result cannot be fixed automatically because its assessment instance id is missing.";
const UNASSIGN_DUPLICATE_RESULTS_ERROR =
  "The duplicate results could not be deleted. Try again in a moment.";

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseName = (value) => normaliseText(value).toLowerCase();

const normalisePrefill = (prefill) => ({
  studentName: normaliseText(prefill?.studentName || prefill?.student_name),
  className: normaliseText(prefill?.className || prefill?.class_name),
  assessmentName: normaliseText(prefill?.assessmentName || prefill?.assessment_name)
});

const normaliseChild = (child, countryGrades) => ({
  ...child,
  id: child?.id || child?._id || child?.studentid || child?.studentId,
  name: child?.name || child?.fullName || child?.displayName || "Unnamed child",
  classId: child?.classId || child?.classid || child?.class_id,
  className: child?.className || child?.class_name || "Class unavailable",
  classDisplayName: formatClassDisplayName(child, "Class unavailable", countryGrades),
  academicYear: child?.academicYear || child?.academic_year || "",
  grade: child?.grade || child?.gradeName || child?.grade_name || ""
});

const getResultId = (result) =>
  result?.id ||
  result?._id ||
  result?.resultId ||
  result?.result_id ||
  result?.assessmentInstanceId ||
  result?.assessment_instance_id ||
  result?.answerid ||
  result?.uuid;

const getResultAssessmentInstanceId = (result) =>
  result?.assessmentInstanceId ||
  result?.assessment_instance_id ||
  result?.assessment_instanceid ||
  result?.assessmentInstanceID ||
  result?.assessment_instance_ID ||
  "";

const getResultName = (result) =>
  result?.name ||
  result?.assessmentName ||
  result?.assessment_name ||
  result?.screenerName ||
  result?.screener_name ||
  result?.title ||
  "Screener result";

const getResultDate = (result) =>
  result?.completedAt ||
  result?.completed_at ||
  result?.completedDate ||
  result?.completed_date ||
  result?.dateCompleted ||
  result?.date_completed ||
  result?.submittedAt ||
  result?.submitted_at ||
  result?.date ||
  result?.createdAt ||
  result?.created_at;

const getResultScoreLabel = (result, fallback = "Completed") => {
  const score =
    result?.standardScore ??
    result?.standard_score ??
    result?.overallScore ??
    result?.overall_score ??
    result?.score ??
    result?.percentage;

  if (score !== undefined && score !== null && score !== "") {
    return `Score ${score}`;
  }

  return result?.status || result?.completionStatus || result?.completion_status || fallback;
};

const getResultAssessmentId = (result) =>
  result?.assessmentId ||
  result?.assessmentid ||
  result?.assessment_id ||
  result?.screenerId ||
  result?.screenerid ||
  result?.screener_id ||
  result?.assessmentTypeId ||
  result?.assessment_type_id ||
  "";

const getResultAcademicYear = (result) =>
  result?.academicYear || result?.academic_year || result?.schoolYear || result?.school_year || "";

const getLocalDuplicateGroupKey = (result) => {
  const assessmentKey =
    normaliseName(getResultAssessmentId(result)) ||
    normaliseName(getResultName(result));
  const yearKey = normaliseName(getResultAcademicYear(result));

  return [assessmentKey, yearKey].filter(Boolean).join("|");
};

const parseDateValue = (dateValue) => {
  if (!dateValue) {
    return null;
  }

  const parsedDate = new Date(dateValue);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatResultDate = (dateValue) => {
  const parsedDate = parseDateValue(dateValue);

  if (!parsedDate) {
    return "Date unavailable";
  }

  return parsedDate.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
};

const normaliseBackendDuplicateGroups = (response, assessmentName) => {
  const groups = Array.isArray(response?.duplicateGroups) ? response.duplicateGroups : [];
  const assessmentNameFilter = normaliseName(assessmentName);

  const getGroupName = (group) =>
    group.assessmentName ||
    group.assessment_name ||
    group.name ||
    group.screenerName ||
    group.screener_name ||
    group.assessmentTitle ||
    group.assessment_title ||
    "Screener result";

  const getGroupId = (group, groupIndex) => group.groupId || group.group_id || group.id || `duplicate-group-${groupIndex}`;

  const getGroupResultItems = (group) => {
    const resultItems = [
      ...(Array.isArray(group.results) ? group.results : []),
      ...(Array.isArray(group.duplicateResults) ? group.duplicateResults : []),
      ...(Array.isArray(group.resultDetails) ? group.resultDetails : []),
      ...(Array.isArray(group.assessmentInstances) ? group.assessmentInstances : []),
      ...(Array.isArray(group.instances) ? group.instances : [])
    ].map((result) => (result && typeof result === "object" ? result : { id: result }));

    const assessmentInstanceIds = [
      ...(Array.isArray(group.assessmentInstanceIds) ? group.assessmentInstanceIds : []),
      ...(Array.isArray(group.assessment_instance_ids) ? group.assessment_instance_ids : [])
    ].filter(Boolean);
    const uniqueAssessmentInstanceIds = [...new Set(assessmentInstanceIds)];
    const fallbackIds = [
      ...(Array.isArray(group.resultIds) ? group.resultIds : []),
      ...(Array.isArray(group.selectedResultIds) ? group.selectedResultIds : [])
    ].filter(Boolean);
    const uniqueFallbackIds = [...new Set(fallbackIds)];

    if (resultItems.length > 0) {
      const resultsById = resultItems.reduce((items, result) => {
        const id = getResultId(result);

        if (id) {
          items[id] = result;
        }

        return items;
      }, {});

      if (uniqueAssessmentInstanceIds.length > resultItems.length) {
        return uniqueAssessmentInstanceIds.map((id) => ({
          ...(resultsById[id] || { id }),
          assessmentInstanceId: id
        }));
      }

      if (uniqueAssessmentInstanceIds.length > 0) {
        return resultItems.map((result, resultIndex) => ({
            ...result,
            assessmentInstanceId:
              getResultAssessmentInstanceId(result) ||
              uniqueAssessmentInstanceIds[resultIndex] ||
              ""
          }));
      }

      return uniqueFallbackIds.length > resultItems.length
        ? uniqueFallbackIds.map((id) => resultsById[id] || { id })
        : resultItems;
    }

    if (uniqueAssessmentInstanceIds.length > 0) {
      return uniqueAssessmentInstanceIds.map((id) => ({
        id,
        assessmentInstanceId: id
      }));
    }

    const fallbackAssessmentInstanceId = group.assessmentInstanceId || group.assessment_instance_id;
    if (fallbackAssessmentInstanceId) {
      return [{
        id: fallbackAssessmentInstanceId,
        assessmentInstanceId: fallbackAssessmentInstanceId
      }];
    }

    const fallbackResultId = group.resultId || group.result_id;
    return (uniqueFallbackIds.length > 0 ? uniqueFallbackIds : [fallbackResultId].filter(Boolean)).map((id) => ({ id }));
  };

  const groupMatchesAssessmentName = (group, filter) => {
    if (!filter) {
      return true;
    }

    return [
      group.name,
      group.assessmentName,
      group.assessment_name,
      group.screenerName,
      group.screener_name,
      group.assessmentId,
      group.assessment_id,
      group.screenerId,
      group.screener_id
    ].some((value) => normaliseName(value) === filter);
  };

  return groups
    .map((group, groupIndex) => {
      const groupId = getGroupId(group, groupIndex);
      const groupName = getGroupName(group);

      return {
        key: groupId,
        groupId,
        name: groupName,
        assessmentId: group.assessmentId || group.assessment_id || "",
        screenerId: group.screenerId || group.screener_id || "",
        reason: group.reason,
        results: getGroupResultItems(group).map((result, resultIndex) => {
          const resultWithFallbacks = {
            ...result,
            assessmentInstanceId: getResultAssessmentInstanceId(result),
            name: result.name || result.assessmentName || result.assessment_name || groupName,
            assessmentName: result.assessmentName || result.assessment_name || group.assessmentName || group.assessment_name,
            screenerName: result.screenerName || result.screener_name || group.screenerName || group.screener_name,
            assessmentId: getResultAssessmentId(result) || group.assessmentId || group.assessment_id,
            academicYear: getResultAcademicYear(result) || group.academicYear || group.academic_year,
            status: result.status || result.completionStatus || result.completion_status || group.status,
            standardScore: result.standardScore ?? result.standard_score ?? group.standardScore ?? group.standard_score,
            score: result.score ?? group.score
          };
          const completedDate = getResultDate(resultWithFallbacks);
          const resultId = getResultId(resultWithFallbacks) || `${groupId}-${resultIndex}`;

          return {
            ...resultWithFallbacks,
            id: resultId,
            name: getResultName(resultWithFallbacks),
            dateLabel: completedDate
              ? formatResultDate(completedDate)
              : result.label || result.resultLabel || getResultAcademicYear(resultWithFallbacks) || `Result ${resultIndex + 1}`,
            scoreLabel: getResultScoreLabel(resultWithFallbacks, group.reason || group.type || "Duplicate result"),
            completedDate,
            groupKey: groupId,
            assessmentId: getResultAssessmentId(resultWithFallbacks),
            assessmentInstanceId: getResultAssessmentInstanceId(resultWithFallbacks),
            resultId,
            academicYear: getResultAcademicYear(resultWithFallbacks)
          };
        }),
        suggestedResultId: getResultId(getGroupResultItems(group)[0]) || null
      };
    })
    .filter((group) => group.results.length > 1)
    .filter((group) => groupMatchesAssessmentName(group, assessmentNameFilter));
};

const buildDuplicateGroupsFromAssessments = (assessments, assessmentName) => {
  const assessmentNameFilter = normaliseName(assessmentName);
  const groupedResults = (Array.isArray(assessments) ? assessments : [])
    .map((assessment, index) => {
      const assessmentInstanceId = getResultAssessmentInstanceId(assessment) || getResultId(assessment);
      const completedDate = getResultDate(assessment);
      const resultId = getResultId(assessment) || assessmentInstanceId || `assessment-${index}`;

      return {
        ...assessment,
        id: resultId,
        assessmentInstanceId,
        name: getResultName(assessment),
        dateLabel: completedDate
          ? formatResultDate(completedDate)
          : assessment?.label || assessment?.resultLabel || getResultAcademicYear(assessment) || `Result ${index + 1}`,
        scoreLabel: getResultScoreLabel(assessment),
        completedDate,
        groupKey: getLocalDuplicateGroupKey(assessment),
        assessmentId: getResultAssessmentId(assessment),
        resultId,
        academicYear: getResultAcademicYear(assessment)
      };
    })
    .filter((result) => result.id && result.assessmentInstanceId && result.groupKey)
    .reduce((groups, result) => {
      if (!groups[result.groupKey]) {
        groups[result.groupKey] = [];
      }

      groups[result.groupKey].push(result);
      return groups;
    }, {});

  return Object.entries(groupedResults)
    .map(([groupKey, results]) => ({
      key: `assessment:${groupKey}`,
      groupId: `assessment:${groupKey}`,
      name: results[0]?.name || "Screener result",
      assessmentId: results[0]?.assessmentId || "",
      screenerId: results[0]?.screenerId || "",
      reason: "Multiple assessment instances exist for this child.",
      results,
      suggestedResultId: results[0]?.id || null
    }))
    .filter((group) => group.results.length > 1)
    .filter((group) => !assessmentNameFilter || normaliseName(group.name) === assessmentNameFilter);
};

const mergeDuplicateGroups = (primaryGroups, fallbackGroups) => {
  const mergedGroups = [...primaryGroups];

  fallbackGroups.forEach((fallbackGroup) => {
    const fallbackInstanceIds = new Set(
      fallbackGroup.results
        .map((result) => result.assessmentInstanceId)
        .filter(Boolean)
    );
    const alreadyIncluded = mergedGroups.some((primaryGroup) => {
      if (primaryGroup.key === fallbackGroup.key || normaliseName(primaryGroup.name) === normaliseName(fallbackGroup.name)) {
        return true;
      }

      return primaryGroup.results.some((result) => fallbackInstanceIds.has(result.assessmentInstanceId));
    });

    if (!alreadyIncluded) {
      mergedGroups.push(fallbackGroup);
    }
  });

  return mergedGroups;
};

const classMatchesPrefill = (child, className) => {
  const targetClassName = normaliseName(className);

  if (!targetClassName) {
    return true;
  }

  return (
    normaliseName(child.className) === targetClassName ||
    normaliseName(child.classDisplayName).includes(targetClassName)
  );
};

const findClearPrefillMatch = (children, childName, className) => {
  const targetChildName = normaliseName(childName);

  if (!targetChildName) {
    return null;
  }

  const exactMatches = children.filter(
    (child) =>
      normaliseName(child.name) === targetChildName &&
      classMatchesPrefill(child, className)
  );

  return exactMatches.length === 1 ? exactMatches[0] : null;
};

const findHiddenDuplicateResult = (selectedGroup, visibleResult) => {
  if (!selectedGroup || !visibleResult) {
    return null;
  }

  const hiddenResults = selectedGroup.results.filter((result) => result.id !== visibleResult.id);

  return hiddenResults[0] || null;
};

const findHiddenDuplicateResults = (selectedGroup, visibleResult) => {
  if (!selectedGroup || !visibleResult) {
    return [];
  }

  return selectedGroup.results.filter((result) => result.id !== visibleResult.id);
};

const isSelectedGroupStillDuplicated = (duplicateGroups, selectedGroup, selectedResultIds) => {
  const selectedResultIdSet = new Set(selectedResultIds);

  return duplicateGroups.some((group) => {
    if (group.key === selectedGroup.key && group.results.length > 1) {
      return true;
    }

    const matchingResultCount = group.results.filter((result) => selectedResultIdSet.has(result.id)).length;
    return matchingResultCount > 1;
  });
};

const getDuplicateResultsSubmitErrorMessage = (submitError) => {
  if (
    submitError?.message === STILL_DUPLICATED_ERROR ||
    submitError?.message === MISSING_ASSESSMENT_INSTANCE_ERROR ||
    submitError?.message === UNASSIGN_DUPLICATE_RESULTS_ERROR
  ) {
    return submitError.message;
  }

  const status = submitError?.response?.status;

  if (status === 400) {
    return "Some required details are missing or invalid. Check the selected results and try again.";
  }

  if (status === 401 || status === 403) {
    return "You do not have access to update these duplicate results.";
  }

  if (status === 404) {
    return "We could not find the selected result. Refresh and choose it again.";
  }

  if (status === 422) {
    return "The duplicate results could not be updated. Check the selected results and try again.";
  }

  return UNASSIGN_DUPLICATE_RESULTS_ERROR;
};

const duplicateCountLabel = (child) => {
  if (child.resultCheckFailed) {
    return "Could not check results";
  }

  const duplicateCount = child.duplicateGroups?.reduce(
    (total, group) => total + group.results.length,
    0
  ) || 0;

  if (duplicateCount === 0) {
    return "No duplicate results";
  }

  return duplicateCount === 2
    ? "2 duplicate results"
    : `${duplicateCount} duplicate results`;
};

function TeacherFixDuplicateResultsModal({
  show,
  handleClose,
  schoolid,
  countryGrades = null,
  prefill = null,
  onViewStudentResults = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("find");
  const [searchTerm, setSearchTerm] = useState("");
  const [matches, setMatches] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedVisibleResultId, setSelectedVisibleResultId] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const prefillAppliedRef = useRef({ child: false, result: false });
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);

  const trimmedSearchTerm = searchTerm.trim();
  const duplicateGroups = useMemo(
    () => selectedChild?.duplicateGroups || [],
    [selectedChild]
  );
  const visibleResult = useMemo(
    () =>
      duplicateGroups
        .flatMap((group) => group.results)
        .find((result) => result.id === selectedVisibleResultId) || null,
    [duplicateGroups, selectedVisibleResultId]
  );
  const selectedGroup = useMemo(
    () =>
      duplicateGroups.find((group) =>
        group.results.some((result) => result.id === selectedVisibleResultId)
      ) || null,
    [duplicateGroups, selectedVisibleResultId]
  );
  const hiddenDuplicateResult = useMemo(
    () => findHiddenDuplicateResult(selectedGroup, visibleResult),
    [selectedGroup, visibleResult]
  );
  const hiddenDuplicateResults = useMemo(
    () => findHiddenDuplicateResults(selectedGroup, visibleResult),
    [selectedGroup, visibleResult]
  );
  const canFixDuplicateResults = Boolean(
    selectedChild &&
    selectedGroup &&
    visibleResult &&
    hiddenDuplicateResults.length > 0
  );

  const resetForm = useCallback(() => {
    setStage("find");
    setSearchTerm(prefillContext.studentName);
    setMatches([]);
    setSelectedChild(null);
    setSelectedVisibleResultId(null);
    setIsSubmitting(false);
    setError("");
    prefillAppliedRef.current = { child: false, result: false };
  }, [prefillContext.studentName]);

  useEffect(() => {
    if (show) {
      resetForm();
    }
  }, [show, resetForm]);

  const loadDuplicateGroupsForChild = useCallback(async (childId) => {
    let duplicateGroups = [];
    let hasLoadedResults = false;
    let firstLoadError = null;

    try {
      const duplicateResponse = await getStudentDuplicateResults(childId);
      duplicateGroups = normaliseBackendDuplicateGroups(
        duplicateResponse,
        prefillContext.assessmentName
      );
      hasLoadedResults = true;
    } catch (duplicateError) {
      firstLoadError = duplicateError;
    }

    if (schoolid) {
      try {
        const assessments = await getAssessmentsByStudentId(schoolid, childId);
        const localDuplicateGroups = buildDuplicateGroupsFromAssessments(
          assessments,
          prefillContext.assessmentName
        );
        duplicateGroups = mergeDuplicateGroups(duplicateGroups, localDuplicateGroups);
        hasLoadedResults = true;
      } catch (assessmentError) {
        firstLoadError = firstLoadError || assessmentError;
      }
    }

    if (!hasLoadedResults) {
      throw firstLoadError || new Error("Duplicate results could not be loaded.");
    }

    return duplicateGroups;
  }, [prefillContext.assessmentName, schoolid]);

  const enrichChildrenWithResults = useCallback(async (children) =>
    Promise.all(
      (Array.isArray(children) ? children : [])
        .map((child) => normaliseChild(child, countryGrades))
        .filter((child) => child.id)
        .map(async (child) => {
          try {
            const duplicateGroupsForChild = await loadDuplicateGroupsForChild(child.id);

            return {
              ...child,
              results: duplicateGroupsForChild.flatMap((group) => group.results),
              duplicateGroups: duplicateGroupsForChild
            };
          } catch (resultError) {
            console.error("Error checking duplicate results:", resultError);

            return {
              ...child,
              results: [],
              duplicateGroups: [],
              resultCheckFailed: true
            };
          }
        })
    ), [loadDuplicateGroupsForChild, countryGrades]);

  useEffect(() => {
    if (!show || trimmedSearchTerm.length < STUDENT_SEARCH_MIN_LENGTH) {
      setMatches([]);
      setSelectedChild(null);
      setSelectedVisibleResultId(null);
      setIsSearching(false);
      return undefined;
    }

    let isCurrentSearch = true;
    setIsSearching(true);
    setError("");

    const timeoutId = window.setTimeout(() => {
      searchTeacherStudents(trimmedSearchTerm, STUDENT_SEARCH_LIMIT)
        .then(enrichChildrenWithResults)
        .then((children) => {
          if (isCurrentSearch) {
            setMatches(children);
            setSelectedChild(null);
            setSelectedVisibleResultId(null);
            setStage("find");
          }
        })
        .catch((searchError) => {
          if (!isCurrentSearch) {
            return;
          }

          console.error("Error searching duplicate results child:", searchError);
          setMatches([]);
          setError("Child search could not be loaded. Please try again.");
        })
        .finally(() => {
          if (isCurrentSearch) {
            setIsSearching(false);
          }
        });
    }, STUDENT_SEARCH_DEBOUNCE_MS);

    return () => {
      isCurrentSearch = false;
      window.clearTimeout(timeoutId);
    };
  }, [show, trimmedSearchTerm, enrichChildrenWithResults]);

  useEffect(() => {
    if (
      !show ||
      prefillAppliedRef.current.child ||
      selectedChild ||
      matches.length < 1
    ) {
      return;
    }

    prefillAppliedRef.current.child = true;
    const matchedChild = findClearPrefillMatch(
      matches,
      prefillContext.studentName,
      prefillContext.className
    );

    if (matchedChild) {
      setSelectedChild(matchedChild);
      setStage(matchedChild.duplicateGroups.length > 0 ? "find" : "noDuplicates");
    }
  }, [
    show,
    matches,
    selectedChild,
    prefillContext.studentName,
    prefillContext.className
  ]);

  useEffect(() => {
    if (
      !show ||
      prefillAppliedRef.current.result ||
      !selectedChild ||
      selectedVisibleResultId
    ) {
      return;
    }

    const prefilledResult =
      duplicateGroups[0]?.results.find((result) => result.id === duplicateGroups[0]?.suggestedResultId) ||
      duplicateGroups[0]?.results[0] ||
      null;
    prefillAppliedRef.current.result = true;

    if (prefilledResult) {
      setSelectedVisibleResultId(prefilledResult.id);
    }
  }, [
    show,
    selectedChild,
    selectedVisibleResultId,
    duplicateGroups
  ]);

  const handleChildSelect = (child) => {
    if (child.resultCheckFailed) {
      return;
    }

    setSelectedChild(child);
    setSelectedVisibleResultId(null);
    setStage(child.duplicateGroups.length > 0 ? "find" : "noDuplicates");
    setError("");
  };

  const handleChildChange = () => {
    setSelectedChild(null);
    setSelectedVisibleResultId(null);
    setStage("find");
    setError("");
  };

  const handleFixDuplicateResults = async () => {
    if (!canFixDuplicateResults || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    const selectedResultIds = selectedGroup.results.map((result) => result.id).filter(Boolean);
    const hiddenAssessmentInstanceIds = hiddenDuplicateResults
      .map((result) => result.assessmentInstanceId)
      .filter(Boolean);

    try {
      if (hiddenAssessmentInstanceIds.length !== hiddenDuplicateResults.length) {
        throw new Error(MISSING_ASSESSMENT_INSTANCE_ERROR);
      }

      await unassignAssessmentBatch(hiddenAssessmentInstanceIds);

      const refreshedDuplicateGroups = await loadDuplicateGroupsForChild(selectedChild.id);

      if (isSelectedGroupStillDuplicated(refreshedDuplicateGroups, selectedGroup, selectedResultIds)) {
        throw new Error(STILL_DUPLICATED_ERROR);
      }

      setStage("success");
    } catch (submitError) {
      console.error("Error fixing duplicate results:", submitError);
      setError(getDuplicateResultsSubmitErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: "",
        message: "Duplicate results fixed."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderSearchResults = () => {
    if (isSearching) {
      return (
        <div className="fix-duplicate-results-search-state">
          <Spinner animation="border" size="sm" />
          <span>Searching children...</span>
        </div>
      );
    }

    if (trimmedSearchTerm.length < STUDENT_SEARCH_MIN_LENGTH) {
      return <p className="fix-duplicate-results-muted">Start typing the child name.</p>;
    }

    if (matches.length < 1) {
      return (
        <div className="fix-duplicate-results-empty">
          <strong>No matching children found.</strong>
          <span>Try another child name.</span>
        </div>
      );
    }

    return matches.map((child) => {
      const isSelected = selectedChild?.id === child.id;
      const isUnavailable = child.resultCheckFailed;

      return (
        <button
          key={child.id}
          type="button"
          className={`fix-duplicate-results-child${isSelected ? " selected" : ""}${isUnavailable ? " disabled" : ""}`}
          onClick={() => handleChildSelect(child)}
          aria-label={`${isUnavailable ? "Unavailable" : "Select"} ${child.name} from ${child.className}. ${duplicateCountLabel(child)}`}
          aria-pressed={isSelected}
          disabled={isUnavailable}
        >
          <strong>{child.name}</strong>
          <span className="fix-duplicate-results-row">
            <span>Class</span>
            <span>{child.classDisplayName || child.className}</span>
          </span>
          {child.academicYear && (
            <span className="fix-duplicate-results-row">
              <span>Year</span>
              <span>{child.academicYear}</span>
            </span>
          )}
          <span className="fix-duplicate-results-row">
            <span>Results</span>
            <span>{duplicateCountLabel(child)}</span>
          </span>
        </button>
      );
    });
  };

  const renderSelectedChild = () => {
    if (!selectedChild) {
      return null;
    }

    return (
      <div className="fix-duplicate-results-selected-child">
        <span className="fix-duplicate-results-pill">Selected</span>
        <strong>{selectedChild.name}</strong>
        <span className="fix-duplicate-results-row">
          <span>Class</span>
          <span>{selectedChild.classDisplayName || selectedChild.className}</span>
        </span>
        <span className="fix-duplicate-results-row">
          <span>Results</span>
          <span>{duplicateCountLabel(selectedChild)}</span>
        </span>
        <Button
          type="button"
          variant="outline-secondary"
          size="sm"
          onClick={handleChildChange}
        >
          Change
        </Button>
      </div>
    );
  };

  const renderResultCard = (group, result) => {
    const isSelected = selectedVisibleResultId === result.id;
    const isSuggested = group.suggestedResultId === result.id;

    return (
      <button
        key={result.id}
        type="button"
        className={`fix-duplicate-results-result${isSelected ? " selected" : ""}`}
        onClick={() => setSelectedVisibleResultId(result.id)}
        aria-label={`Keep ${result.dateLabel} ${result.name} visible`}
        aria-pressed={isSelected}
      >
        <span className="fix-duplicate-results-card-header">
          <strong>{result.dateLabel}</strong>
          {isSuggested && <span className="fix-duplicate-results-pill">Suggested</span>}
        </span>
        <span className="fix-duplicate-results-row">
          <span>Assessment</span>
          <span>{result.name}</span>
        </span>
        {result.academicYear && (
          <span className="fix-duplicate-results-row">
            <span>Year</span>
            <span>{result.academicYear}</span>
          </span>
        )}
        <span className="fix-duplicate-results-row">
          <span>Score/status</span>
          <span>{result.scoreLabel}</span>
        </span>
        {result.assessmentId && (
          <span className="fix-duplicate-results-row">
            <span>Assessment ID</span>
            <span>{result.assessmentId}</span>
          </span>
        )}
        {result.resultId && (
          <span className="fix-duplicate-results-row">
            <span>Result ID</span>
            <span>{result.resultId}</span>
          </span>
        )}
        <span className="fix-duplicate-results-action-text">
          {isSelected ? "Will stay visible" : "Choose to keep visible"}
        </span>
      </button>
    );
  };

  const renderDuplicateGroups = () => (
    <div className="fix-duplicate-results-groups">
      {duplicateGroups.map((group) => (
        <div key={group.key} className="fix-duplicate-results-group">
          <strong>{group.name}</strong>
          <div className="fix-duplicate-results-results">
            {group.results.map((result) => renderResultCard(group, result))}
          </div>
        </div>
      ))}
    </div>
  );

  const renderSelectionSummary = () => {
    if (!selectedChild || !visibleResult || !hiddenDuplicateResult) {
      return null;
    }

    return (
      <div className="fix-duplicate-results-summary">
        <strong>{visibleResult.dateLabel} will stay visible.</strong>
        <span>
          The duplicate {hiddenDuplicateResult.dateLabel} result will be deleted.
        </span>
        <span>{selectedChild.name} stays in {selectedChild.className}.</span>
      </div>
    );
  };

  const renderFind = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Fix Duplicate Results</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="fix-duplicate-results-intro">
          Find the child, then choose the result that should stay visible.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group controlId="teacherFixDuplicateResultsChildSearch">
          <Form.Label>Which child has duplicate results?</Form.Label>
          {!selectedChild && (
            <>
              <Form.Text as="div" className="fix-duplicate-results-field-label">
                Search child
              </Form.Text>
              <Form.Control
                type="search"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setSelectedChild(null);
                  setSelectedVisibleResultId(null);
                  setError("");
                }}
                aria-label="Search child with duplicate results"
              />
            </>
          )}
        </Form.Group>

        <div className="fix-duplicate-results-children" aria-label="Matching children">
          {selectedChild ? renderSelectedChild() : renderSearchResults()}
        </div>

        {selectedChild && duplicateGroups.length > 0 && (
          <div className="fix-duplicate-results-section">
            <Form.Label as="div">Which result should stay visible?</Form.Label>
            {renderDuplicateGroups()}
            {renderSelectionSummary()}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <span className="fix-duplicate-results-footer-note">
          {canFixDuplicateResults
            ? `${visibleResult.dateLabel} selected.`
            : "Choose a child and visible result."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleFixDuplicateResults}
          disabled={!canFixDuplicateResults || isSubmitting}
        >
          {isSubmitting ? "Fixing..." : "Fix duplicate results"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderNoDuplicates = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">No Duplicate Results Found</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="fix-duplicate-results-intro">
          {selectedChild?.name || "This child"} has one result for each assessment.
        </p>
        <div className="fix-duplicate-results-empty">
          <strong>There is nothing to fix for this child.</strong>
          <span>The result list does not show repeated assessments. Check another child or go back.</span>
        </div>
      </Modal.Body>
      <Modal.Footer className="fix-duplicate-results-no-duplicates-footer">
        <span className="fix-duplicate-results-footer-note">
          Choose another child to keep checking.
        </span>
        <Button variant="outline-secondary" onClick={() => setStage("find")}>
          Back
        </Button>
        <Button variant="outline-secondary" onClick={resetForm}>
          Search another child
        </Button>
        <Button variant="primary" disabled>
          Fix duplicate results
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Duplicate Results Fixed</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="fix-duplicate-results-intro">
          The duplicate results action was completed.
        </p>
        <Alert variant="success" className="fix-duplicate-results-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>
              The selected result is now the visible result.
            </strong>
            <span>
              {visibleResult?.dateLabel} result is visible. Duplicate results were deleted.
            </span>
          </span>
        </Alert>
        <div className="fix-duplicate-results-summary">
          <strong>What changed</strong>
          <span className="fix-duplicate-results-row">
            <span>Visible</span>
            <span>{visibleResult?.dateLabel} {visibleResult?.name}</span>
          </span>
          <span className="fix-duplicate-results-row">
            <span>Deleted</span>
            <span>
              {hiddenDuplicateResults.length === 1
                ? `${hiddenDuplicateResults[0]?.dateLabel} ${hiddenDuplicateResults[0]?.name}`
                : `${hiddenDuplicateResults.length} duplicate results`}
            </span>
          </span>
          <span className="fix-duplicate-results-row">
            <span>Child</span>
            <span>{selectedChild?.name} remains in {selectedChild?.className}.</span>
          </span>
          <span className="fix-duplicate-results-row">
            <span>Other results</span>
            <span>Other assessments were not changed.</span>
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer className="fix-duplicate-results-success-footer">
        <span className="fix-duplicate-results-footer-note">
          {embedded ? "Done returns to SENSAI." : "Done returns to Help Centre home."}
        </span>
        <Button variant="outline-secondary" onClick={resetForm}>
          Fix another duplicate
        </Button>
        {!embedded && onViewStudentResults && (
          <Button
            variant="outline-secondary"
            onClick={() => onViewStudentResults()}
          >
            View student results
          </Button>
        )}
        <Button variant="primary" onClick={handleDone}>
          Done
        </Button>
      </Modal.Footer>
    </>
  );

  const renderStage = () => {
    if (stage === "noDuplicates") {
      return renderNoDuplicates();
    }

    if (stage === "success") {
      return renderSuccess();
    }

    return renderFind();
  };

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      className="teacher-fix-duplicate-results-modal"
      backdrop="static"
    >
      {renderStage()}
    </Modal>
  );
}

export default TeacherFixDuplicateResultsModal;
